import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BottomNav from '../components/BottomNav';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Package, Building2, Lightbulb } from 'lucide-react';

// Timeline data for Goods & Infrastructure Projects
const GOODS_INFRA_TIMELINE = [
  { activity: 'Pre-Procurement Conference', ra9184Min: 1, ra9184Max: 'Whenever necessary', ra12009Min: 1, ra12009Max: 'IRR will provide', operational: '-' },
  { activity: 'Advertising/Publication/Posting of Invitation to Bid', ra9184Min: 7, ra9184Max: 7, ra12009Min: 7, ra12009Max: 7, operational: 'Day 1 to 7' },
  { activity: 'Pre-Bid Conference', ra9184Min: 12, ra9184Max: 'Whenever necessary', ra12009Min: 12, ra12009Max: 12, operational: 'Day 8 to Day 19' },
  { activity: 'Deadline for Submission and Receipt of Bids/Bid Opening', ra9184Min: 1, ra9184Max: 45, ra12009Min: 1, ra12009Max: 1, operational: 'Day 20' },
  { activity: 'Bid Evaluation', ra9184Min: 1, ra9184Max: 7, ra12009Min: 1, ra12009Max: 7, operational: 'Day 21 to 27' },
  { activity: 'Post-Qualification', ra9184Min: 2, ra9184Max: 45, ra12009Min: 2, ra12009Max: 12, operational: 'Day 28 to 39' },
  { activity: "BAC's Resolution Recommending Award/ HoPE's Approval/ Issuance of NoA", ra9184Min: 1, ra9184Max: 15, ra12009Min: 1, ra12009Max: 10, operational: 'Day 40 – 49' },
  { activity: 'Contract Preparation and Signing', ra9184Min: 1, ra9184Max: 10, ra12009Min: 1, ra12009Max: 10, operational: 'Day 50 – 60' },
  { activity: 'Approval of contract by higher authority', ra9184Min: 1, ra9184Max: 30, ra12009Min: 1, ra12009Max: 20, operational: 'Day 61 – 75 or 80' },
  { activity: 'Issuance of Notice to Proceed', ra9184Min: 1, ra9184Max: 7, ra12009Min: 1, ra12009Max: 3, operational: 'Day 81 – 83' }
];

// Timeline data for Consulting Services
const CONSULTING_TIMELINE = [
  { activity: 'Pre-Procurement Conference', ra9184Min: 1, ra9184Max: 'Whenever necessary', ra12009Min: 1, ra12009Max: 'IRR will provide', operational: '-' },
  { activity: 'Advertising/Publication/Posting of Invitation to Bid', ra9184Min: 7, ra9184Max: 7, ra12009Min: 7, ra12009Max: 7, operational: 'Day 1 to 7' },
  { activity: 'Eligibility Check and Shortlisting', ra9184Min: 7, ra9184Max: 20, ra12009Min: 7, ra12009Max: 20, operational: 'Day 8 to 27' },
  { activity: 'Pre-Bid Conference', ra9184Min: 12, ra9184Max: 'Whenever necessary', ra12009Min: 12, ra12009Max: 12, operational: 'Day 28 to Day 39' },
  { activity: 'Deadline for Submission and Receipt of Bids', ra9184Min: 1, ra9184Max: 75, ra12009Min: 1, ra12009Max: 60, operational: 'Day 40 to Day 67 (Max)' },
  { activity: 'Bid Opening', ra9184Min: 1, ra9184Max: 1, ra12009Min: 1, ra12009Max: 1, operational: 'Day 67' },
  { activity: 'Bid Evaluation', ra9184Min: 1, ra9184Max: 21, ra12009Min: 1, ra12009Max: 15, operational: 'Day 68 to 82' },
  { activity: 'Approval of Ranking by the HoPE', ra9184Min: 1, ra9184Max: 2, ra12009Min: 1, ra12009Max: 1, operational: 'Day 83' },
  { activity: 'Notification for Negotiation', ra9184Min: 1, ra9184Max: 3, ra12009Min: 1, ra12009Max: 1, operational: 'Day 84' },
  { activity: 'Negotiation', ra9184Min: 1, ra9184Max: 10, ra12009Min: 1, ra12009Max: 7, operational: 'Day 85 to 91' },
  { activity: 'Post-Qualification', ra9184Min: 2, ra9184Max: 30, ra12009Min: 2, ra12009Max: 15, operational: 'Day 92 to 106' },
  { activity: "BAC's Resolution Recommending Award/ HoPE's Approval/ Issuance of NoA", ra9184Min: 1, ra9184Max: 15, ra12009Min: 1, ra12009Max: 10, operational: 'Day 107 – 116' },
  { activity: 'Contract Preparation and Signing', ra9184Min: 1, ra9184Max: 10, ra12009Min: 1, ra12009Max: 10, operational: 'Day 117 – 126' },
  { activity: 'Approval of contract by higher authority', ra9184Min: 1, ra9184Max: 30, ra12009Min: 1, ra12009Max: 20, operational: 'Day 127 to 141 or 146' },
  { activity: 'Issuance of Notice to Proceed', ra9184Min: 1, ra9184Max: 7, ra12009Min: 1, ra12009Max: 3, operational: 'Day 142 – 144' }
];

export default function TimelineMatrixPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('goods');

  const calculateTotals = (data) => {
    let ra9184Min = 0;
    let ra9184Max = 0;
    let ra12009Min = 0;
    let ra12009Max = 0;
    let ra9184MinExcl = 0;
    let ra9184MaxExcl = 0;
    let ra12009MinExcl = 0;
    let ra12009MaxExcl = 0;

    data.forEach((row) => {
      const isHigherAuth = row.activity.toLowerCase().includes('higher authority');
      
      // Add to total
      if (typeof row.ra9184Min === 'number') {
        ra9184Min += row.ra9184Min;
        if (!isHigherAuth) ra9184MinExcl += row.ra9184Min;
      }
      if (typeof row.ra12009Min === 'number') {
        ra12009Min += row.ra12009Min;
        if (!isHigherAuth) ra12009MinExcl += row.ra12009Min;
      }
      
      // For max, only add if it's a number
      if (typeof row.ra9184Max === 'number') {
        ra9184Max += row.ra9184Max;
        if (!isHigherAuth) ra9184MaxExcl += row.ra9184Max;
      }
      if (typeof row.ra12009Max === 'number') {
        ra12009Max += row.ra12009Max;
        if (!isHigherAuth) ra12009MaxExcl += row.ra12009Max;
      }
    });

    return { 
      ra9184Min, ra9184Max, ra12009Min, ra12009Max,
      ra9184MinExcl, ra9184MaxExcl, ra12009MinExcl, ra12009MaxExcl 
    };
  };

  const renderTimelineTable = (data, title) => {
    const totals = calculateTotals(data);
    
    return (
      <div className="space-y-4">
        <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-4">{title}</h3>
        
        {/* Mobile scroll hint */}
        <p className="text-xs text-slate-500 dark:text-slate-400 md:hidden mb-2 flex items-center gap-1">
          <Lightbulb className="w-3 h-3 flex-shrink-0" />
          Scroll horizontally to see all columns →
        </p>
        
        <div className="overflow-x-auto border rounded-lg">
          <table className="w-full min-w-[800px] border-collapse">
            <thead>
              <tr className="bg-emerald-600 text-white">
                <th className="border border-emerald-700 px-4 py-3 text-left font-semibold">Activity</th>
                <th className="border border-emerald-700 px-3 py-3 text-center font-semibold" colSpan="2">RA 9184<br/>(Calendar Days)</th>
                <th className="border border-emerald-700 px-3 py-3 text-center font-semibold" colSpan="2">RA 12009<br/>(Calendar Days)</th>
                <th className="border border-emerald-700 px-3 py-3 text-center font-semibold">RA 12009<br/>Operational Timeline</th>
              </tr>
              <tr className="bg-emerald-500 text-white">
                <th className="border border-emerald-600 px-4 py-2"></th>
                <th className="border border-emerald-600 px-3 py-2 text-center text-sm">Min</th>
                <th className="border border-emerald-600 px-3 py-2 text-center text-sm">Max</th>
                <th className="border border-emerald-600 px-3 py-2 text-center text-sm">Min</th>
                <th className="border border-emerald-600 px-3 py-2 text-center text-sm">Max</th>
                <th className="border border-emerald-600 px-3 py-2 text-center text-sm">Max Period</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, index) => (
                <tr key={index} className={`${index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-slate-50 dark:bg-slate-700'} hover:bg-emerald-50 dark:hover:bg-emerald-900/30 transition-colors`}>
                  <td className="border border-slate-300 dark:border-slate-600 px-4 py-3 text-slate-700 dark:text-slate-300">{row.activity}</td>
                  <td className="border border-slate-300 dark:border-slate-600 px-3 py-3 text-center text-slate-700 dark:text-slate-300">{row.ra9184Min}</td>
                  <td className="border border-slate-300 dark:border-slate-600 px-3 py-3 text-center text-slate-700 dark:text-slate-300">{row.ra9184Max}</td>
                  <td className="border border-slate-300 dark:border-slate-600 px-3 py-3 text-center text-emerald-700 dark:text-emerald-400 font-semibold">{row.ra12009Min}</td>
                  <td className="border border-slate-300 dark:border-slate-600 px-3 py-3 text-center text-emerald-700 dark:text-emerald-400 font-semibold">{row.ra12009Max}</td>
                  <td className="border border-slate-300 dark:border-slate-600 px-3 py-3 text-center text-blue-700 dark:text-blue-400 font-semibold">{row.operational || '-'}</td>
                </tr>
              ))}
              {/* Total Row */}
              <tr className="bg-amber-100 dark:bg-amber-900/40 font-bold">
                <td className="border border-amber-300 dark:border-amber-700 px-4 py-3 text-slate-800 dark:text-slate-200">TOTAL (Including Higher Authority)</td>
                <td className="border border-amber-300 dark:border-amber-700 px-3 py-3 text-center text-slate-800 dark:text-slate-200">{totals.ra9184Min}</td>
                <td className="border border-amber-300 dark:border-amber-700 px-3 py-3 text-center text-slate-800 dark:text-slate-200">{totals.ra9184Max}</td>
                <td className="border border-amber-300 dark:border-amber-700 px-3 py-3 text-center text-emerald-800 dark:text-emerald-300">{totals.ra12009Min}</td>
                <td className="border border-amber-300 dark:border-amber-700 px-3 py-3 text-center text-emerald-800 dark:text-emerald-300">{totals.ra12009Max}</td>
                <td className="border border-amber-300 dark:border-amber-700 px-3 py-3 text-center">-</td>
              </tr>
              <tr className="bg-blue-100 dark:bg-blue-900/40 font-bold">
                <td className="border border-blue-300 dark:border-blue-700 px-4 py-3 text-slate-800 dark:text-slate-200">TOTAL (Excluding Higher Authority)</td>
                <td className="border border-blue-300 dark:border-blue-700 px-3 py-3 text-center text-slate-800 dark:text-slate-200">{totals.ra9184MinExcl}</td>
                <td className="border border-blue-300 dark:border-blue-700 px-3 py-3 text-center text-slate-800 dark:text-slate-200">{totals.ra9184MaxExcl}</td>
                <td className="border border-blue-300 dark:border-blue-700 px-3 py-3 text-center text-emerald-800 dark:text-emerald-300">{totals.ra12009MinExcl}</td>
                <td className="border border-blue-300 dark:border-blue-700 px-3 py-3 text-center text-emerald-800 dark:text-emerald-300">{totals.ra12009MaxExcl}</td>
                <td className="border border-blue-300 dark:border-blue-700 px-3 py-3 text-center">-</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-emerald-50 dark:from-slate-900 dark:via-slate-800 dark:to-emerald-900 pb-24">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/library')}
          className="mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl lg:text-5xl font-bold text-slate-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'IBM Plex Sans' }}>
            Competitive Bidding Process Timelines
          </h1>
          <p className="text-slate-600 dark:text-slate-400">Comparative timeline analysis: RA 9184 vs RA 12009</p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6 dark:bg-slate-800">
            <TabsTrigger value="goods" className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              Goods & Infrastructure Projects
            </TabsTrigger>
            <TabsTrigger value="consulting" className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Consulting Services
            </TabsTrigger>
          </TabsList>

          {/* Goods & Infrastructure Tab */}
          <TabsContent value="goods">
            <Card className="dark:bg-slate-800 dark:border-slate-700">
              <CardHeader>
                <CardTitle className="dark:text-slate-100">Bidding Process Timeline - Goods & Infrastructure Projects</CardTitle>
              </CardHeader>
              <CardContent>
                {renderTimelineTable(GOODS_INFRA_TIMELINE, '')}
                
                {/* Summary */}
                <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg">
                  <h4 className="font-semibold text-blue-900 dark:text-blue-200 mb-3">Important Note</h4>
                  <div className="text-sm text-blue-800 dark:text-blue-300 space-y-3">
                    <p>
                      The following periods from the last day of posting of the Invitation to Bid or Request for Expression of Interest up to the submission and receipt of bids shall be observed:
                    </p>
                    <p className="ml-4">
                      a) For Goods, a maximum period of forty-five (45) calendar days.
                    </p>
                    <div className="ml-4">
                      <p className="mb-2">b) For Infrastructure Projects, the following maximum periods:</p>
                      <table className="ml-6 border-collapse">
                        <thead>
                          <tr className="border-b border-blue-300 dark:border-blue-700">
                            <th className="text-left py-1 pr-8">ABC (in Philippine Peso)</th>
                            <th className="text-left py-1">Period</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="py-1 pr-8">Fifty (50) million and below</td>
                            <td className="py-1">50 calendar days</td>
                          </tr>
                          <tr>
                            <td className="py-1 pr-8">Above fifty (50) million</td>
                            <td className="py-1">65 calendar days</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <p className="ml-4">
                      c) For Consulting Services, a maximum period of seventy-five (75) calendar days.
                    </p>
                    <p className="mt-3 font-semibold">
                      Reference: Sec. 54.5 of RA 12009 IRR
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Consulting Services Tab */}
          <TabsContent value="consulting">
            <Card className="dark:bg-slate-800 dark:border-slate-700">
              <CardHeader>
                <CardTitle className="dark:text-slate-100">Bidding Process Timeline - Consulting Services</CardTitle>
              </CardHeader>
              <CardContent>
                {renderTimelineTable(CONSULTING_TIMELINE, '')}
                
                {/* Summary */}
                <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg">
                  <h4 className="font-semibold text-blue-900 dark:text-blue-200 mb-3">Important Note</h4>
                  <div className="text-sm text-blue-800 dark:text-blue-300 space-y-3">
                    <p>
                      The following periods from the last day of posting of the Invitation to Bid or Request for Expression of Interest up to the submission and receipt of bids shall be observed:
                    </p>
                    <p className="ml-4">
                      a) For Goods, a maximum period of forty-five (45) calendar days.
                    </p>
                    <div className="ml-4">
                      <p className="mb-2">b) For Infrastructure Projects, the following maximum periods:</p>
                      <table className="ml-6 border-collapse">
                        <thead>
                          <tr className="border-b border-blue-300 dark:border-blue-700">
                            <th className="text-left py-1 pr-8">ABC (in Philippine Peso)</th>
                            <th className="text-left py-1">Period</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="py-1 pr-8">Fifty (50) million and below</td>
                            <td className="py-1">50 calendar days</td>
                          </tr>
                          <tr>
                            <td className="py-1 pr-8">Above fifty (50) million</td>
                            <td className="py-1">65 calendar days</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <p className="ml-4">
                      c) For Consulting Services, a maximum period of seventy-five (75) calendar days.
                    </p>
                    <p className="mt-3 font-semibold">
                      Reference: Sec. 54.5 of RA 12009 IRR
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <BottomNav />
    </div>
  );
}
